import { IHtmlHighlight } from "~/typings/annotate/annotation";

const MARGIN_BETWEEN_THREAD = 20;

export function extractMentionDataFromStr(str: string) {
  // the value of the markup is defined at https://github.com/signavio/react-mentions#configuration

  // WE updated, let's not break old one, but new one are simpler
  const newMarkupRegex = /@\[([a-zA-Z0-9_]*)\]/gm;
  // eslint-disable-next-line lingui/no-unlocalized-strings -- markdown link template
  str = str.replace(newMarkupRegex, "[@$1](/profile/$1)");

  const markupRegex = /\+\[(.*?)\]\((.*?)\)/g;
  const ids = [];
  let match;
  let plainTextValue = str;

  while ((match = markupRegex.exec(str)) !== null) {
    const [wholeMatch, displayName, id] = match;

    ids.push(id);
    plainTextValue = plainTextValue.replace(
      // keep the @ and only replace the remaining text
      wholeMatch,
      "@" + displayName,
    );
  }

  return {
    plainTextValue,
    ids,
  };
}

export function computeThreadPosition(
  highlights: IHtmlHighlight[],
  parentContainer: HTMLElement,
  selectedThread: string | undefined,
) {
  const positions = highlights
    .reduce(
      (prev, x) => {
        if (!x.comments.length && x.id !== selectedThread) {
          return prev;
        }

        let highlightID: string = x.id || "";

        const highlightElements = document.getElementsByClassName(
          // eslint-disable-next-line lingui/no-unlocalized-strings -- DOM class name prefix
          "thread_" + highlightID,
        );
        const threadElement = document.getElementById(
          // eslint-disable-next-line lingui/no-unlocalized-strings -- DOM id prefix
          "commentthread_" + highlightID,
        );
        if (!highlightID || !threadElement || !highlightElements?.length) {
          return prev;
        }
        highlightID = highlightID as string;
        const rectangle = [...highlightElements]
          .reduce((e) => e)
          .getBoundingClientRect();
        let expectedPosition =
          rectangle.top - parentContainer.getBoundingClientRect().top;
        if (!prev.length) {
          return [
            {
              top: expectedPosition,
              height: threadElement.clientHeight,
              id: highlightID,
            },
          ];
        }

        const lastElement = prev[prev.length - 1];
        const lastEnd =
          lastElement.top + lastElement.height + MARGIN_BETWEEN_THREAD;

        if (selectedThread !== highlightID) {
          let nextPosition = {
            top: Math.max(expectedPosition, lastEnd),
            height: threadElement.clientHeight,
            id: highlightID,
          };
          return [...prev, nextPosition];
        }
        // Now is the fun part. If this is the selected thread, we want it to be exactly at the position needed.
        // Therefore, if something would be overlapping, we'll either place them or after.
        // The algorithm is as follow:
        // If the max between expectedPosition and lastEnd is expectedPosition, we can put it there directly.
        // If not, there are 2 options:
        // - There is enough space above the current element, in that case we'll _push up_ the previous elements
        // - There is not enough space above the current element, in that case we'll push down some elements until there is enough space.
        if (Math.max(expectedPosition, lastEnd) === expectedPosition) {
          // It is already at the correct position. All good!
          let nextPosition = {
            top: Math.max(expectedPosition, lastEnd),
            height: threadElement.clientHeight,
            id: highlightID,
          };
          return [...prev, nextPosition];
        }

        // We are forcing this position
        let nextPosition = {
          top: expectedPosition,
          height: threadElement.clientHeight,
          id: highlightID,
        };
        let spacedUsedAbove = prev.reduce(
          (prev, x) => prev + x.height + MARGIN_BETWEEN_THREAD,
          0,
        );

        // Let's reverse the array first
        prev.reverse();
        let elementsToMoveAfter = [] as any;

        while (spacedUsedAbove > expectedPosition) {
          // If it uses too much space above, we'll first move reminders element to recompute.
          let items = prev.splice(0, 1);
          elementsToMoveAfter = [...elementsToMoveAfter, ...items];
          spacedUsedAbove = prev.reduce(
            (prev, x) => prev + x.height + MARGIN_BETWEEN_THREAD,
            0,
          );
        }
        // Let's move stuff above now
        if (spacedUsedAbove < expectedPosition) {
          // We can move stuff before, nothing will move after.
          // Let's now go in reverse until there is no more conflicts. and move
          let currentTop = nextPosition.top;
          for (let i = 0; i < prev.length; i++) {
            const current = prev[i];
            current.top = currentTop - MARGIN_BETWEEN_THREAD - current.height;
            currentTop = current.top;
            if (i + 1 >= prev.length) {
              break;
            }
            let previousElement = prev[i + 1];
            if (
              previousElement.top +
                previousElement.height +
                MARGIN_BETWEEN_THREAD <
              currentTop
            ) {
              // No need to push anything anymore, we can break
              break;
            }
          }
          prev.reverse();
        }

        // We can now re-add the elements that needed to move to after, and recompute their new position.
        let nextTop =
          nextPosition.top + nextPosition.height + MARGIN_BETWEEN_THREAD;
        for (let i = 0; i < elementsToMoveAfter.length; i++) {
          elementsToMoveAfter[i].top = nextTop;
          nextTop =
            nextTop + elementsToMoveAfter[i].height + MARGIN_BETWEEN_THREAD;
        }

        return [...prev, nextPosition, ...elementsToMoveAfter];
      },
      [] as { top: number; height: number; id: string }[],
    )
    .reduce(
      (previous, next) => ({ ...previous, [next.id]: next.top }),
      {} as { [key: string]: number },
    );
  return positions;
}

export function getOrderedCommentsThread(
  highlights: IHtmlHighlight[],
): IHtmlHighlight[] {
  const orderedHighlights = [...highlights].sort((h1, h2) => {
    if (
      h1.startParentID === undefined ||
      h2.startParentID === undefined ||
      h1.startOffset === undefined ||
      h2.startOffset === undefined
    ) {
      console.error(
        "getOrderedCommentsThread: invalid highlight detected",
        h1,
        h2,
      );
      return 0;
    }
    let startParentID = h1.startParentID;
    let endParentID = h2.startParentID;
    if (startParentID < endParentID) {
      return -1;
    }
    if (startParentID > endParentID) {
      return 1;
    }
    if (startParentID == endParentID && h1.startOffset < h2.startOffset) {
      return -1;
    }
    return 1;
  });
  return orderedHighlights;
}

export function isElementInViewport(el: HTMLElement) {
  const rect = el.getBoundingClientRect();

  return (
    rect.top >= 0 &&
    rect.left >= 0 &&
    rect.bottom <=
      (window.innerHeight ||
        document.documentElement.clientHeight) /* or $(window).height() */ &&
    rect.right <=
      (window.innerWidth ||
        document.documentElement.clientWidth) /* or $(window).width() */
  );
}
